import { useState } from 'react';
import { useChat } from '../store';
import { api } from '../api';
import { colorForId, initials } from '../utils/colors';

interface AvatarProps {
  id: string;
  displayName: string | null;
  email: string;
  size?: number;
  online?: boolean;
  /** Override of the global lookup — pass when rendering for a non-workspace user. */
  avatarUpdatedAt?: string | null;
  /**
   * Discriminator for the URL builder. Defaults to 'user' (existing behavior:
   * GET /api/users/:id/avatar). 'conversation' switches to the group photo
   * endpoint and 'channel' switches to the channel photo endpoint —
   * callers MUST pass `avatarUpdatedAt` explicitly for non-user kinds
   * because those avatars aren't tracked in the user store.
   */
  kind?: 'user' | 'conversation' | 'channel';
  /**
   * For channel kind: an emoji shortcut renders in place of the avatar
   * when no uploaded image exists. Centered, sized proportionally.
   */
  emoji?: string | null;
}

export function Avatar({
  id,
  displayName,
  email,
  size = 36,
  online,
  avatarUpdatedAt,
  kind = 'user',
  emoji,
}: AvatarProps) {
  // If the caller didn't pass an explicit avatar version, fall back to the
  // workspace user store so any avatar update propagates everywhere.
  const fromStore = useChat((s) => {
    if (kind !== 'user') return null;
    if (avatarUpdatedAt !== undefined) return null;
    return s.users.find((u) => u.id === id)?.avatarUpdatedAt ?? null;
  });
  const meAvatar = useChat((s) => {
    if (kind !== 'user') return null;
    if (avatarUpdatedAt !== undefined) return null;
    if (id !== s.me?.id) return null;
    return s.me?.avatarUpdatedAt ?? null;
  });
  const version = avatarUpdatedAt ?? meAvatar ?? fromStore;
  const url = version
    ? kind === 'conversation'
      ? api.conversationAvatarUrl(id, version)
      : kind === 'channel'
        ? api.channelAvatarUrl(id, version)
        : api.avatarUrl(id, version)
    : '';

  const bg = colorForId(id);
  const text = initials(displayName, email);
  const fontSize = Math.round(size * 0.4);
  const [imgFailed, setImgFailed] = useState(false);

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      {url && !imgFailed ? (
        <img
          src={url}
          alt={displayName ?? email}
          width={size}
          height={size}
          onError={() => setImgFailed(true)}
          className="w-full h-full rounded-full object-cover bg-zinc-900 hairline"
        />
      ) : kind === 'channel' && emoji ? (
        // Emoji-as-avatar — bigger font than initials so the glyph
        // reads at a glance. We center within the same colored disc
        // for a consistent footprint.
        <div
          className="w-full h-full rounded-full flex items-center justify-center select-none"
          style={{ backgroundColor: bg, fontSize: Math.round(size * 0.55) }}
          aria-hidden="true"
        >
          {emoji}
        </div>
      ) : (
        <div
          className="w-full h-full rounded-full flex items-center justify-center font-semibold text-white select-none"
          style={{ backgroundColor: bg, fontSize }}
          aria-hidden="true"
        >
          {text}
        </div>
      )}
      {online !== undefined && (
        <span
          className={`absolute bottom-0 right-0 block rounded-full ring-2 ring-zinc-950 ${
            online ? 'bg-emerald-500' : 'bg-zinc-600'
          }`}
          style={{
            width: Math.max(8, size * 0.28),
            height: Math.max(8, size * 0.28),
          }}
          aria-label={online ? 'online' : 'offline'}
        />
      )}
    </div>
  );
}
